import { useId, useState, useEffect } from "react";
import { Link, Navigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Helmet } from "react-helmet-async";
import { useAuth } from "@/context/AuthContext";
import { clearOnboarding, readOnboarding } from "@/lib/onboarding-context";
import { ArrowRight, ChevronRight, TrendingUp, Users, BarChart2, Trophy } from "lucide-react";
import { PublicAuthControl } from "@/components/auth/PublicAuthControl";
import { NextSeasonBanner } from "@/components/ladder/NextSeasonBanner";
import { fetchOpenLadders } from "@/lib/services/ladders";

const capabilities = ["Elo Rating", "Form Analysis", "Head-to-Head", "Partner Synergy"];

const secondaryTools = [
  { title: "Rankings", description: "Elo leaderboard", href: "/players/leaderboard", icon: TrendingUp },
  { title: "Partnerships", description: "Partner synergy", href: "/players/partnerships", icon: Users },
  { title: "Interleague", description: "Season standings", href: "/interleague/results", icon: BarChart2 },
  { title: "Events", description: "Tournament data", href: "/events/intelligence", icon: Trophy },
];

const CHART_POINTS: [number, number][] = [
  [0, 98], [28, 82], [56, 90], [84, 68], [112, 74],
  [140, 52], [168, 42], [196, 58], [224, 28], [252, 14],
];

const chartPath = CHART_POINTS.map(([x, y], i) => `${i === 0 ? "M" : "L"}${x},${y}`).join(" ");
const areaPath = `${chartPath} L252,120 L0,120 Z`;

const ChartLabel = () => (
  <p className="text-center text-[8px] font-medium uppercase tracking-wider text-muted-foreground/40">
    Rating over time
  </p>
);

const EloSparkline = ({ className = "" }: { className?: string }) => {
  const gradId = `sparkGrad-${useId().replace(/:/g, "")}`;
  return (
    <svg
      viewBox="0 0 260 120"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden="true"
      preserveAspectRatio="xMidYMid meet"
    >
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="currentColor" stopOpacity="0.2" />
          <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
        </linearGradient>
      </defs>
      {[30, 60, 90].map((y) => (
        <line key={y} x1="0" y1={y} x2="260" y2={y} stroke="currentColor" strokeOpacity="0.08" strokeWidth="1" />
      ))}
      <path d={areaPath} fill={`url(#${gradId})`} />
      <path d={chartPath} stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
      {CHART_POINTS.map(([x, y]) => (
        <circle key={`${x}-${y}`} cx={x} cy={y} r="2.5" fill="currentColor" />
      ))}
    </svg>
  );
};

const Index = () => {
  const { user, isLoading } = useAuth();
  // Declared before the redirect below: hooks cannot sit after a conditional return, and the
  // redirect is conditional.
  const openLadders = useQuery({
    queryKey: ["open-ladders"],
    queryFn: fetchOpenLadders,
    staleTime: 5 * 60_000,
    // A signed-in visitor is redirected below and never sees this page, so there is no point
    // fetching for them. The hook itself still runs unconditionally.
    enabled: !user,
  });
  const featured = (openLadders.data ?? [])[0];

  // Read once. Clearing happens in an effect so StrictMode's double render cannot read back an
  // emptied store and send a returning player to /home.
  const [returnTo] = useState<string | null>(() => {
    const r = readOnboarding()?.returnTo;
    return r && r.startsWith("/") && !r.startsWith("//") ? r : null;
  });
  useEffect(() => {
    if (!isLoading && user && returnTo) clearOnboarding();
  }, [isLoading, user, returnTo]);

  // Signed-in visitors start from their home, or from wherever they were heading when they signed
  // in. Supabase falls back to the Site URL (this page) when the OAuth callback is not allow-listed,
  // so the stored return path is the only thing that gets an invited player back to registration.
  if (!isLoading && user) return <Navigate to={returnTo ?? "/home"} replace />;
  return (
    <div className="min-h-screen bg-background text-foreground">
      <Helmet>
        <title>Scoreglass - Padel Tournaments & Rankings</title>
        <meta name="description" content="Run padel tournaments, track ELO rankings, and follow live results - all in one place." />
        <meta property="og:title" content="Scoreglass - Padel Tournaments & Rankings" />
        <meta property="og:description" content="Run padel tournaments, track ELO rankings, and follow live results - all in one place." />
        <meta property="og:url" content="https://scoreglass.app" />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://scoreglass.app" />
      </Helmet>
      <div className="h-0.5 bg-primary" />

      <header className="border-b border-border/40 px-6 py-3 flex items-center justify-between">
        <span className="text-sm font-bold tracking-tight text-foreground">ScoreGlass</span>
        <PublicAuthControl />
      </header>

      <main className="container mx-auto max-w-3xl px-6 py-14 md:py-20">

        {/* Hero */}
        <div className="mb-12 animate-in fade-in slide-in-from-bottom-4 duration-500">
          <div className="flex items-start gap-8 md:gap-12">
            <div className="flex-1 space-y-4">
              <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-muted-foreground/50">
                Performance analytics
              </p>
              <h1 className="text-5xl font-bold leading-[1.05] tracking-tight md:text-6xl">
                Your game,<br />fully tracked.
              </h1>
              <p className="max-w-sm text-sm text-muted-foreground md:text-base">
                Results, ratings, and insights from every match you've played.
              </p>
              <div className="flex flex-wrap gap-1.5 pt-1">
                {capabilities.map((cap) => (
                  <span
                    key={cap}
                    className="rounded border border-border bg-card/60 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground"
                  >
                    {cap}
                  </span>
                ))}
              </div>
            </div>

            {/* Desktop chart — beside text */}
            <div className="hidden md:flex w-44 shrink-0 flex-col gap-1 pt-6 text-primary/50">
              <EloSparkline className="h-28 w-full" />
              <ChartLabel />
            </div>
          </div>

          {/* Mobile chart — below text */}
          <div className="mt-6 md:hidden text-primary/50">
            <EloSparkline className="w-full aspect-[260/120]" />
            <div className="mt-1"><ChartLabel /></div>
          </div>
        </div>

        {/* A season taking registrations is the most useful thing this page can offer a visitor
            who has never used ScoreGlass, so it sits above the product tour. */}
        {/* The banner returns null once its deadline passes, which can happen mid-session on a
            stale query - so the wrapper's margin and animation must not outlive it. */}
        {featured && featured.registrationClosesAt && Date.now() < new Date(featured.registrationClosesAt).getTime() && (
          <div style={{ animationDelay: "100ms" }} className="mb-3 animate-in fade-in slide-in-from-bottom-2 duration-500 fill-mode-both">
            <NextSeasonBanner ladder={featured} />
          </div>
        )}

        {/* Primary — Deep Dive */}
        <div style={{ animationDelay: "200ms" }} className="mb-3 animate-in fade-in slide-in-from-bottom-2 duration-500 fill-mode-both">
          <Link
            to="/players/dive"
            className="group relative block overflow-hidden rounded-xl border border-border bg-card transition-all hover:border-primary/40 hover:bg-card/80"
          >
            <div className="absolute inset-y-0 left-0 w-[3px] bg-primary" />
            <div className="flex items-start justify-between gap-4 p-6 pl-7">
              <div className="space-y-2">
                <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-primary">
                  Start here
                </p>
                <h2 className="text-xl font-semibold tracking-tight md:text-2xl">
                  Player Deep Dive
                </h2>
                <p className="text-xs text-muted-foreground md:text-sm">
                  Your complete performance profile - rating trajectory, head-to-head records,
                  recent form, and partner tendencies.
                </p>
              </div>
              <ArrowRight className="mt-0.5 h-4 w-4 shrink-0 text-primary transition-transform group-hover:translate-x-1" />
            </div>
          </Link>
        </div>

        {/* Secondary tools — 2×2 grid */}
        <div style={{ animationDelay: "300ms" }} className="mb-6 animate-in fade-in duration-500 fill-mode-both overflow-hidden rounded-xl border border-border/50">
          <div className="grid grid-cols-2 gap-px bg-border/30">
            {secondaryTools.map((tool) => {
              const Icon = tool.icon;
              return (
                <Link
                  key={tool.title}
                  to={tool.href}
                  className="group flex items-center gap-3 bg-background px-4 py-4 transition-colors hover:bg-card"
                >
                  <Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground/60 transition-colors group-hover:text-primary" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium leading-tight">{tool.title}</p>
                    <p className="text-xs text-muted-foreground/70">{tool.description}</p>
                  </div>
                  <ChevronRight className="ml-auto h-3 w-3 shrink-0 text-muted-foreground/20 transition-colors group-hover:text-primary/40" />
                </Link>
              );
            })}
          </div>
        </div>

        {/* Organizer entry */}
        <div style={{ animationDelay: "400ms" }} className="animate-in fade-in duration-500 fill-mode-both">
          <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground/40">
            For organizers
          </p>
          <Link
            to="/organizer"
            className="group relative block overflow-hidden rounded-xl border border-border/50 bg-card/30 transition-all hover:border-border hover:bg-card/60"
          >
            <div className="absolute inset-y-0 left-0 w-[3px] bg-muted-foreground/25" />
            <div className="flex items-start justify-between gap-4 p-5 pl-7">
              <div className="space-y-1">
                <p className="text-sm font-semibold">Organizer workspace</p>
                <p className="text-xs text-muted-foreground">
                  Create events, configure divisions, and run live control - all from one place.
                </p>
              </div>
              <ArrowRight className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground/40 transition-transform group-hover:translate-x-1 group-hover:text-foreground" />
            </div>
          </Link>
        </div>

      </main>
    </div>
  );
};

export default Index;
